import { DealRibbon } from '@app/category-deals-list/model/product.model';
import { DealDto, FacilityDto } from '@app/model/deal.model';
import { getPathFromProductUrl } from '@app/utilities/url.utils';

export interface ProductCardEntry {
    id: number;
    productUrl: string;
    productPath: string;
    imageUrl: string;
    bannerImages?: Array<string>;
    name: string;
    shortDescription: string;
    price?: number;
    specialPrice: number;
    starCount?: number;
    average?: number;
    nrOfReviews: number;
    startDate?: Date;
    endDate?: Date;
    ribbon?: DealRibbon;
    categoryId: number;
    percentDiscount?: string;
    townName?: string;
    facilityNames: string[];
    allInclusiveName?: string;
}

export const transformDealDtoToProductCardEntry = (
    dealDto: DealDto
): ProductCardEntry => {
    return {
        id: dealDto.dealId,
        productUrl: dealDto.url,
        productPath: getPathFromProductUrl(dealDto.url),
        categoryId: dealDto.categoryId,
        price: dealDto.baseCrossedOutPrice,
        ribbon: dealDto.ribbon,
        imageUrl: dealDto.images[0]?.imgUrl,
        bannerImages: dealDto.images.slice(0, 5).map((image) => image.imgUrl),
        name: dealDto.dealTitle,
        shortDescription: dealDto.subTitle,
        specialPrice: dealDto.baseSellingPrice,
        starCount: dealDto.starRating,
        average: dealDto.outletReviews?.reviewScoreCard?.reviewsScore / 2,
        nrOfReviews: dealDto.outletReviews?.totalReviewCount || 0,
        townName: dealDto.townName,
        // TODO: When we get these from the backend they have to be set properly
        startDate: undefined,
        endDate: undefined,
        facilityNames:
            filterFacilies(
                getIdMap(dealDto.categoryId) || [],
                dealDto.outletFacilities.facilities
            ) || [],
        allInclusiveName: extractFacilityNameById(
            dealDto.outletFacilities.facilities,
            757
        ),
    };

};

const getIdMap = (categoryId: number): number[][] => {
    switch (categoryId) {
        case 3: // Hotels
            return facilityIdMapForHotels;
        case 4: // Restaurants
            return facilityIdMapForRestaurants;
        case 8:
            return facilityIdMapForDayPackage;
    }
};

const filterFacilies = (
    listOfIds: number[][],
    facilities: FacilityDto[]
): string[] => {
    return findMatchingObjects(listOfIds, facilities).slice(0, 5);
};

const findMatchingObjects = (
    listOfLists: number[][],
    objects: FacilityDto[]
): string[] => {
    const objectMap = new Map<number, FacilityDto>();
    objects.forEach((obj) => objectMap.set(obj.facilityId, obj));

    const results: FacilityDto[] = [];

    for (const idList of listOfLists) {
        for (const id of idList) {
            const match = objectMap.get(id);
            if (match) {
                results.push(match);
                break; // move to next internal list after first match
            }
        }
    }

    return results.map((obj) => obj.facilityName);
};

const extractFacilityNameById = (
    facilities: FacilityDto[],
    id: number
): string | undefined => {
    return facilities.find((f) => f.facilityId === id)?.facilityName;
};

const facilityIdMapForHotels: number[][] = [
    // This list is ordered by display priority for facility pills.
    // The first matching facility in the list will be shown first on the UI.
    // hotels
    [566, 35], // Kids Club
    [581], // Golf
    [242, 580], // Beach bar
    [74, 610, 1, 583, 202], // Kids pool, infinity pool, pool, swimming pool, adults pool
    [232, 326, 41], // Water Skiing, Sailing, Kayak
    [600, 601], // Hammam, sauna
    [233, 206], // Snorkeling, diving
    [603], // Fitness
    [602], // Spa
    [209], // Parking
    [597], // Beach volleyball
    [757], // All Inclusive

    // SPA
    [48], // Couples
    [424], // Facials
    [478], // Manicures
    [466, 469], // Waxing
    [489], // Scrub
    [488], // Ayuverda
    [607], // Beauty Parolur
    [640], // Sauna/ Hammam
    [605], // Jacuzzi
];

const facilityIdMapForDayPackage: number[][] = [
    [28, 602], // Spa facilities - facility, not necessarily spa pass in the DP
    [603], // Fitness center
    [576, 8], // Restaurant
    [242, 579, 245], // Beach bar, Bar/Lounge
    [74, 610, 1, 583, 202], // Kids pool, infinity pool, pool, swimming pool, adults pool
    [566, 35], // Kids Club
    [233], // Snorkeling
    [220], // Archery
    [41], // Kayak
    [29], // Tennis
    [249], // Parking
    [331], // Wake-boarding
    [232], // Water Skiing
    [594], // Beach Volleyball
    [592], // Bocce Ball
    [328], // Kite Surfing
    [251], // Taxi service
];

const facilityIdMapForRestaurants: number[][] = [
    [400], // Seafood
    [391], // Vegan
    [423], // Gluten Free
    [391], // Italian
    [398], // Mauritian
    [397], // Chinese
    [399], // American
    [388], // European
    [394], // Indian
    [389], // Asian
    [396], // Thai
];
